import Logo from '@anm/components/Logo';
import isMobile from '@anm/helpers/is/isMobile';
import noop from '@anm/helpers/noop';
import * as semrush from '@anm/helpers/semrush';
import { useScrollDirection } from '@anm/hooks/useScrollingUp';
import { memo, useEffect, useState, FC } from 'react';
import { useSelector } from 'react-redux';

import vtConfig from '../../../config';
import routes from '../../../routes';
import { projectSelectors } from '../../../store/modules/project';
import { userSelectors } from '../../../store/modules/user';
import LeftBarLayout from '../../layouts/LeftBarLayout';
import useCheckDestinations from '../MyStreamsSection/hooks/useCheckDestinations';
import MyProjectWorkspaceDropdown from '../workspace/MyProjectWorkspaceDropdown';

import ProfileMenuDropdown from './ProfileMenuDropdown';
import ResourcesMenuDropdown from './ResourcesMenuDropdown';
import { resourcesNavList } from './ResourcesMenuDropdown/DropdownContent';
import SectionWrapper, { LeftBarNavBottomContent, LinkWrapper } from './Wrapper';
import { getProjectsTabs } from './helpers';
import { useFetchRootVideosHandler, useFetchStreamsUpcoming, useReFetchUploads } from './hooks';
import { MyProjectSectionProps } from './types';

export * from './types';

const { Link } = routes;

const {
  urls: { wave: waveUrl }
} = vtConfig();

const MyProjectsSection: FC<MyProjectSectionProps> = ({
  activeTab,
  isSemrush,
  currentTab,
  onTabChange,
  hideMobileNavigationDropdown
}) => {
  const canLiveStream = useSelector(userSelectors.selectCanLiveStream);
  const hasTeamAccess = useSelector(userSelectors.selectTeamAccess);
  const fetchRootFolder = useFetchRootVideosHandler();
  const fetchStreams = useFetchStreamsUpcoming();
  const fetchUploads = useReFetchUploads();
  const isSearchOpen = useSelector(projectSelectors.selectSearchOpenStatus);

  useCheckDestinations();

  const scrollDirection = useScrollDirection();

  const isSemrushIframe = semrush.isSemrushIframe() || isSemrush;

  const renderNavTopContent = isSemrushIframe
    ? undefined
    : () => (
        <div>
          <Logo variant="dark-wave" waveUrl={waveUrl} />
          {hasTeamAccess && <MyProjectWorkspaceDropdown />}
        </div>
      );

  const [topMargin, setTopMargin] = useState(30);

  useEffect(() => {
    if (isMobile()) return;
    isSemrushIframe && setTopMargin(-17);
  }, [isSemrushIframe]);

  return (
    <SectionWrapper
      {...{ scrollDirection: !isSearchOpen && scrollDirection, hideMobileNavigationDropdown }}
      topMargin={topMargin}
    >
      <LeftBarLayout
        variant="full-size"
        navButtonVariant="white-shadow"
        activeButton={activeTab}
        onActiveButtonChange={noop}
        list={getProjectsTabs({
          activeTab,
          currentTab,
          canLiveStream,
          fetchRootFolder,
          fetchStreams,
          fetchUploads,
          onTabChange
        })}
        renderNavTopContent={renderNavTopContent}
        renderNavBottomContent={() => (
          <LeftBarNavBottomContent key={topMargin}>
            {!isSemrushIframe && <ResourcesMenuDropdown list={resourcesNavList} />}
            {isSemrushIframe ? (
              <LinkWrapper>
                <Link route="subscription">
                  <a data-omit="true">Subscription</a>
                </Link>
              </LinkWrapper>
            ) : (
              <ProfileMenuDropdown />
            )}
          </LeftBarNavBottomContent>
        )}
      />
    </SectionWrapper>
  );
};

const MyProjectsSectionMemorized = memo(MyProjectsSection);

export default MyProjectsSectionMemorized;
